import { ref, onMounted, watch, onUnmounted, nextTick, unref, toRef, isRef, computed, type Ref } from 'vue';

type MaybeRef<T> = T | { value: T };
type MaybeGetter<T> = MaybeRef<T> | (() => T);

export function toComputed<T>(input: MaybeGetter<T>) {
  if (typeof input === 'function') {
    return computed<T>(input as () => T);
  }
  if (isRef(input)) {
    return input as Ref<T>;
  }
  return ref<T>(input as T);
}

export const calculateAspectRatio = (
  w: number | undefined,
  h: number | undefined,
): number => {
  if (!w || !h) {
    return 1;
  }
  if (h === 0) {
    return 0; // Avoid division by zero
  }
  return w / h;
};

export type RotationModes = 'force' | 'auto' | 'none' | -90 | 0 | 90 | 180; // | 'portrait' | 'landscape';
export type RotationClasses = '' | 'rotate-counterclockwise' | 'rotate-clockwise' | 'rotate-180';

export function useRotator<MANUALLY_UPDATE extends boolean>(
  wrapper: MaybeGetter<HTMLElement | null>,
  preferClockwise: MaybeGetter<boolean>,
  rotation: MaybeGetter<RotationModes>,
  manuallyUpdateSize: MaybeGetter<MANUALLY_UPDATE>,
  aspectRatio: MANUALLY_UPDATE extends true ? MaybeGetter<number> : Ref<number>,
) {
  const wrapperRef = toComputed(wrapper);
  const preferClockwiseRef = toComputed(preferClockwise);
  const rotationRef = toComputed(rotation);
  const manuallyUpdateSizeRef = toComputed(manuallyUpdateSize);
  const aspectRatioRef = toComputed(aspectRatio);

  const rotationClass = ref<RotationClasses>('');
  let resizeObserver: ResizeObserver | null = null;
  const width = ref(0);
  const height = ref(0);
  const degrees = ref(0);
  const transform = computed(() => !degrees.value ? '' : `rotate(${degrees.value}deg)`);

  function formatAspectRatio(aspectRatio: number) {
    if (!aspectRatio) {
      return 'inherit';
    } if (aspectRatio > 1) {
      // Aspect ratio is wider than tall
      return `${aspectRatio.toFixed(2)} / 1`;
    }
      // Aspect ratio is taller than wide or square
      return `1 / ${(1 / aspectRatio).toFixed(2)}`;
  }

  const updateAspectRatio = (w: number, h: number) => {
    if (!isRef(aspectRatio) || manuallyUpdateSizeRef.value) {
      return;
    }
    aspectRatioRef.value = calculateAspectRatio(w, h);
  };

  const updateElementSizeAndRotation = () => {
    const wrapperEl = unref(wrapperRef);
    if (!wrapperEl) return;
    // await nextTick();
    // await awaitableRequestAnimationFrame();

    if (!manuallyUpdateSizeRef.value) {
      const box = wrapperEl.getBoundingClientRect();
      width.value = box.width;
      height.value = box.height;
      updateAspectRatio(box.width, box.height);
    }
    updateRotation();
  };

  const updateRotation = () => {
    const windowAspectRatio = window.innerWidth / window.innerHeight;
    const needsRotation = (windowAspectRatio > 1) !== (aspectRatioRef.value > 1);
    const rotate = (
      typeof rotationRef.value === 'number'
        ? true
        : rotationRef.value === 'none'
            ? false
            : rotationRef.value === 'force'
                ? true
                : needsRotation
    );
    console.log('rotate', {
      rotation: rotationRef.value,
      rotate,
      windowAspectRatio,
      ratio: aspectRatioRef.value,
      '(windowAspectRatio > 1)': (windowAspectRatio > 1),
      '(aspectRatioRef.value > 1)': (aspectRatioRef.value > 1),
      needsRotation,
    });
    /*
    aspectRatioRef.value = (
        rotate
            ? 1 / aspectRatioRef.value
            : aspectRatioRef.value
    );
   */

    if (rotate) {
      degrees.value = (
          typeof rotationRef.value === 'number'
              ? rotationRef.value
              : preferClockwiseRef.value
                  ? 90
                  : -90
      );
    } else {
      degrees.value = 0;
    }
    rotationClass.value = {
        [-90]: 'rotate-counterclockwise',
        0: '',
        90: 'rotate-clockwise',
        180: 'rotate-180',
        force: preferClockwiseRef.value ? 'rotate-clockwise' : 'rotate-counterclockwise',
        auto: !rotation ? '' : preferClockwiseRef.value ? 'rotate-clockwise' : 'rotate-counterclockwise',
        none: '',
      }[degrees.value as RotationModes] as RotationClasses;
  };

  onMounted(async () => {
    window.addEventListener('resize', updateRotation);

    // Set up ResizeObserver to monitor slot size changes
    resizeObserver = new ResizeObserver(() => {
      updateElementSizeAndRotation();
    });

    const wrapperEl = unref(wrapperRef);
    if (wrapperEl) {
      resizeObserver.observe(wrapperEl);
    }

    updateRotation();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateRotation);
    const wrapperEl = unref(wrapperRef);
    if (resizeObserver && wrapperEl) {
      resizeObserver.unobserve(wrapperEl);
    }
    resizeObserver = null;
  });

  watch(rotationRef, updateElementSizeAndRotation);
  watch(preferClockwiseRef, updateRotation);

  return {
    // Expose refs for external use
    rotation: rotationRef,
    wrapper: wrapperRef,
    preferClockwise: preferClockwiseRef,
    manuallyUpdateSize: manuallyUpdateSizeRef,
    aspectRatio: aspectRatioRef,
    // Methods
    updateRotation,
    updateElementSizeAndRotation,
    // Computed properties
    rotationClass,
    aspectRatioString: computed(() => formatAspectRatio(aspectRatioRef.value)),
    width,
    height,
    degrees,
    transform,
  };
}
